import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const KSeFSettings = () => {
  const [settings, setSettings] = useState({
    enabled: false,
    environment: 'test',
    api_token: '',
    api_token_masked: '',
    has_token: false,
    nip: '',
    auto_send: false,
    // Pobieranie faktur zakupu z KSeF (nabywca) - ustawienie ksef_pobieranie_zakupow '0'/'1'
    pobieranie_zakupow: false
  });
  // Ostatnia synchronizacja faktur zakupu z KSeF (GET /ksef/otrzymane/podsumowanie)
  const [syncZakupow, setSyncZakupow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState(null);
  const [showToken, setShowToken] = useState(false);
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    fetchSettings();
    fetchStats();
    fetchLogs();
    fetchSyncZakupow();
  }, []);

  const fetchSyncZakupow = async () => {
    try {
      const response = await api.get('/ksef/otrzymane/podsumowanie', { silent: true });
      const data = response.data;
      if (data?.success) {
        const d = data.data || {};
        // kontrakt GET /ksef/otrzymane/podsumowanie: ostatnia_synchronizacja (stan), sync_w_toku_id, ustawienia.pobieranie_zakupow
        setSyncZakupow({ ostatnia: d.ostatnia_synchronizacja || null, w_toku: d.sync_w_toku_id || null });
        // Gdy GET /ksef/settings nie zna jeszcze pola, stan przelacznika bierzemy z podsumowania
        if (typeof d.ustawienia?.pobieranie_zakupow === 'boolean') {
          setSettings(prev => (typeof prev.pobieranie_zakupow_z_ustawien === 'boolean' ? prev : { ...prev, pobieranie_zakupow: d.ustawienia.pobieranie_zakupow }));
        }
      }
    } catch (error) {
      // modul faktur z KSeF moze nie byc wdrozony - przelacznik i tak dziala
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await api.get('/ksef/settings');
      const data = response.data;
      if (data?.success) {
        const d = data.data || {};
        const zUstawien = typeof d.pobieranie_zakupow === 'boolean';
        setSettings(prev => ({ ...prev, ...d, ...(zUstawien ? { pobieranie_zakupow_z_ustawien: true } : {}) }));
      }
    } catch (error) {
      console.error('Blad pobierania ustawien KSeF:', error);
      // Nie pokazuj toastu/komunikatu jesli to 401/403 — interceptor obsluzy
      if (!error?.isAuthError && !error?.isPermissionError) {
        setMessage({ type: 'error', text: error?.message || 'Nie mozna pobrac ustawien' });
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/ksef/stats');
      const data = response.data;
      if (data?.success) {
        setStats(data.data?.stats || null);
      }
    } catch (error) {
      console.error('Blad pobierania statystyk KSeF:', error);
    }
  };

  const fetchLogs = async () => {
    try {
      const response = await api.get('/ksef/logs?limit=10');
      const data = response.data;
      if (data?.success) {
        setLogs(Array.isArray(data.data) ? data.data : []);
      }
    } catch (error) {
      console.error('Blad pobierania logow KSeF:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);

    try {
      const response = await api.post('/ksef/settings', {
          enabled: settings.enabled,
          environment: settings.environment,
          api_token: settings.api_token || undefined,
          nip: settings.nip,
          auto_send: settings.auto_send,
          pobieranie_zakupow: settings.pobieranie_zakupow
        });

      const data = response.data;
      if (data?.success) {
        setMessage({ type: 'success', text: 'Ustawienia zapisane!' });
        // Wyczysc lokalne pole tokenu po zapisie (token jest juz na serwerze, maskowany)
        setSettings(prev => ({ ...prev, api_token: '' }));
        fetchSettings();
      } else {
        setMessage({ type: 'error', text: data?.error || data?.message || 'Blad zapisu' });
      }
    } catch (error) {
      // api.js interceptor przepakowuje na plain Error z .status i .message
      if (error?.isAuthError || error?.isPermissionError) {
        // interceptor obsluzyl
        return;
      }
      const msg = error?.message || 'Blad polaczenia z serwerem';
      setMessage({ type: 'error', text: msg });
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setMessage(null);

    try {
      const response = await api.post('/ksef/test-connection', null, { timeout: 60000 });

      const data = response.data;
      if (data?.success) {
        setMessage({ type: 'success', text: data.message || 'Polaczenie OK' });
      } else {
        setMessage({ type: 'error', text: data?.error || data?.message || 'Blad polaczenia' });
      }
    } catch (error) {
      if (error?.isAuthError || error?.isPermissionError) {
        return;
      }
      const msg = error?.message || 'Blad polaczenia z serwerem';
      setMessage({ type: 'error', text: msg });
    } finally {
      setTesting(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      accepted: { bg: 'var(--success-light)', color: 'var(--success)', text: 'Zaakceptowane' },
      pending: { bg: 'var(--warning-light)', color: 'var(--warning)', text: 'Oczekujace' },
      sent: { bg: 'var(--info-light)', color: 'var(--info)', text: 'Wyslane' },
      rejected: { bg: 'var(--danger-light)', color: 'var(--danger)', text: 'Odrzucone' },
      error: { bg: 'var(--danger-light)', color: 'var(--danger)', text: 'Blad' }
    };
    const style = styles[status] || styles.pending;
    return (
      <span style={{
        padding: '0.25rem 0.5rem',
        borderRadius: '0.25rem',
        backgroundColor: style.bg,
        color: style.color,
        fontSize: '0.85rem'
      }}>
        {style.text}
      </span>
    );
  };

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Ladowanie...</div>;
  }

  return (
    <div style={{ padding: '1.5rem' }}>
      <h2 style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <span style={{ fontSize: '1.5rem' }}>&#128196;</span>
        KSeF - Krajowy System e-Faktur
      </h2>

      {message && (
        <div style={{
          padding: '1rem',
          marginBottom: '1rem',
          borderRadius: '0.5rem',
          backgroundColor: message.type === 'success' ? 'var(--success-light)' : 'var(--danger-light)',
          color: message.type === 'success' ? 'var(--success)' : 'var(--danger)',
          border: `1px solid ${message.type === 'success' ? 'var(--success-light)' : 'var(--danger-light)'}`
        }}>
          {message.text}
        </div>
      )}

      {/* Glowny przelacznik */}
      <div style={{
        backgroundColor: settings.enabled ? 'var(--success-light)' : 'var(--bg)',
        border: `1px solid ${settings.enabled ? 'var(--success-light)' : 'var(--border)'}`,
        borderRadius: '0.5rem',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          cursor: 'pointer'
        }}>
          <input
            type="checkbox"
            checked={settings.enabled}
            onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
            style={{ width: '24px', height: '24px' }}
          />
          <div>
            <span style={{ fontWeight: '600', fontSize: '1.1rem' }}>
              {settings.enabled ? 'Integracja KSeF wlaczona' : 'Integracja KSeF wylaczona'}
            </span>
            <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginTop: '0.25rem' }}>
              {settings.enabled
                ? 'Faktury beda wysylane do Krajowego Systemu e-Faktur'
                : 'Wlacz aby rozpoczac wysylanie faktur do KSeF'}
            </div>
          </div>
        </label>
      </div>

      {/* Ustawienia API */}
      <div style={{
        backgroundColor: "var(--card-bg)",
        border: '1px solid var(--border)',
        borderRadius: '0.5rem',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <h3 style={{ marginBottom: '1rem', fontSize: '1.1rem' }}>
          Ustawienia API KSeF
        </h3>

        <div style={{ display: 'grid', gap: '1rem' }}>
          {/* Srodowisko */}
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Srodowisko
            </label>
            <select
              value={settings.environment}
              onChange={(e) => setSettings({ ...settings, environment: e.target.value })}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid var(--border)',
                borderRadius: '0.25rem',
                fontSize: '1rem'
              }}
            >
              <option value="test">Testowe (ksef-test.mf.gov.pl)</option>
              <option value="demo">Demo (ksef-demo.mf.gov.pl)</option>
              <option value="production">Produkcyjne (ksef.mf.gov.pl)</option>
            </select>
            {settings.environment === 'production' && (
              <div style={{
                marginTop: '0.5rem',
                padding: '0.5rem',
                backgroundColor: 'var(--warning-light)',
                borderRadius: '0.25rem',
                color: 'var(--warning)',
                fontSize: '0.9rem'
              }}>
                UWAGA: Korzystasz ze srodowiska produkcyjnego! Faktury beda wysylane do oficjalnego systemu KSeF.
              </div>
            )}
          </div>

          {/* Token API */}
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Token autoryzacyjny KSeF
            </label>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <input
                type={showToken ? 'text' : 'password'}
                value={settings.api_token || ''}
                onChange={(e) => setSettings({ ...settings, api_token: e.target.value })}
                placeholder={settings.has_token ? settings.api_token_masked : 'Wprowadz token z portalu KSeF...'}
                style={{
                  flex: 1,
                  padding: '0.5rem',
                  border: '1px solid var(--border)',
                  borderRadius: '0.25rem'
                }}
              />
              <button
                onClick={() => setShowToken(!showToken)}
                style={{
                  padding: '6px 12px',
                  fontSize: '12px',
                  border: '1px solid var(--border)',
                  backgroundColor: 'var(--bg)',
                  cursor: 'pointer'
                }}
              >
                {showToken ? '&#128584;' : '&#128065;'}
              </button>
            </div>
            <small style={{ color: 'var(--text-muted)', marginTop: '0.25rem', display: 'block' }}>
              Token wygenerujesz w portalu KSeF (ksef.mf.gov.pl) w sekcji "Tokeny autoryzacyjne"
            </small>
          </div>

          {/* NIP */}
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              NIP firmy
            </label>
            <input
              type="text"
              value={settings.nip || ''}
              onChange={(e) => setSettings({ ...settings, nip: e.target.value })}
              placeholder="Np. 1234567890"
              maxLength="13"
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid var(--border)',
                borderRadius: '0.25rem'
              }}
            />
            <small style={{ color: 'var(--text-muted)', marginTop: '0.25rem', display: 'block' }}>
              NIP firmy, dla ktorej wygenerowano token autoryzacyjny
            </small>
          </div>

          {/* Auto-send */}
          <div>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={settings.auto_send}
                onChange={(e) => setSettings({ ...settings, auto_send: e.target.checked })}
                style={{ width: '18px', height: '18px' }}
              />
              <span style={{ fontWeight: '500' }}>Automatycznie wysylaj faktury do KSeF</span>
            </label>
            <small style={{ color: 'var(--text-muted)', marginLeft: '1.75rem' }}>
              Kazda nowa faktura zostanie automatycznie wyslana do KSeF po utworzeniu
            </small>
          </div>

          {/* Pobieranie faktur zakupu z KSeF (nabywca) */}
          <div data-testid="ksef-pobieranie-zakupow">
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={Boolean(settings.pobieranie_zakupow)}
                onChange={(e) => setSettings({ ...settings, pobieranie_zakupow: e.target.checked })}
                style={{ width: '18px', height: '18px' }}
              />
              <span style={{ fontWeight: '500' }}>Pobieraj faktury zakupu z KSeF (nabywca)</span>
            </label>
            <small style={{ color: 'var(--text-muted)', marginLeft: '1.75rem', display: 'block' }}>
              Faktury wystawione na nasz NIP będą co jakiś czas pobierane z KSeF do zakładki „Faktury z KSeF” (do dodania jako faktury zakupu albo koszty).
              {' '}
              <span data-testid="ksef-ostatnia-sync-zakupow">
                Ostatnie pobranie: {syncZakupow?.w_toku ? 'trwa…' : (syncZakupow?.ostatnia?.zakonczono
                  ? `${new Date(syncZakupow.ostatnia.zakonczono).toLocaleString('pl-PL')}${syncZakupow.ostatnia.status === 'blad' ? ` (błąd: ${syncZakupow.ostatnia.komunikat || 'nieznany'})` : ` (pobrano ${syncZakupow.ostatnia.pobrano ?? 0}, nowych ${syncZakupow.ostatnia.nowych ?? 0})`}`
                  : 'nigdy')}
              </span>
            </small>
          </div>

          {/* Przyciski */}
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                border: '1px solid var(--success)', color: 'var(--success)' ,
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.7 : 1
              }}
            >
              {saving ? 'Zapisywanie...' : '&#128190; Zapisz ustawienia'}
            </button>
            <button
              onClick={handleTestConnection}
              disabled={testing || !settings.has_token}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                border: '1px solid var(--info)', color: 'var(--info)' ,
                cursor: (testing || !settings.has_token) ? 'not-allowed' : 'pointer',
                opacity: (testing || !settings.has_token) ? 0.7 : 1
              }}
            >
              {testing ? 'Testowanie...' : '&#128268; Testuj polaczenie'}
            </button>
          </div>
        </div>
      </div>

      {/* Statystyki */}
      {stats && (
        <div style={{
          backgroundColor: "var(--card-bg)",
          border: '1px solid var(--border)',
          borderRadius: '0.5rem',
          padding: '1.5rem',
          marginBottom: '1.5rem'
        }}>
          <h3 style={{ marginBottom: '1rem', fontSize: '1.1rem' }}>
            &#128202; Statystyki KSeF
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '1rem'
          }}>
            <div style={{
              padding: '1rem',
              backgroundColor: 'var(--bg)',
              borderRadius: '0.25rem',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--success)' }}>
                {stats.accepted || 0}
              </div>
              <div style={{ color: 'var(--text-muted)' }}>Zaakceptowanych</div>
            </div>
            <div style={{
              padding: '1rem',
              backgroundColor: 'var(--bg)',
              borderRadius: '0.25rem',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--warning)' }}>
                {(stats.pending || 0) + (stats.sent || 0)}
              </div>
              <div style={{ color: 'var(--text-muted)' }}>Oczekujacych</div>
            </div>
            <div style={{
              padding: '1rem',
              backgroundColor: 'var(--bg)',
              borderRadius: '0.25rem',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--danger)' }}>
                {(stats.rejected || 0) + (stats.error || 0)}
              </div>
              <div style={{ color: 'var(--text-muted)' }}>Bledow</div>
            </div>
            <div style={{
              padding: '1rem',
              backgroundColor: 'var(--bg)',
              borderRadius: '0.25rem',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--info)' }}>
                {stats.total || 0}
              </div>
              <div style={{ color: 'var(--text-muted)' }}>Wszystkich</div>
            </div>
          </div>
        </div>
      )}

      {/* Ostatnie logi */}
      {logs.length > 0 && (
        <div style={{
          backgroundColor: "var(--card-bg)",
          border: '1px solid var(--border)',
          borderRadius: '0.5rem',
          padding: '1.5rem',
          marginBottom: '1.5rem'
        }}>
          <h3 style={{ marginBottom: '1rem', fontSize: '1.1rem' }}>
            &#128220; Ostatnie operacje
          </h3>
          <div className="admin-table-wrap" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Data</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Operacja</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Status</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Faktura</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '0.5rem' }}>
                      {new Date(log.created_at).toLocaleString('pl-PL')}
                    </td>
                    <td style={{ padding: '0.5rem' }}>{log.operation_type}</td>
                    <td style={{ padding: '0.5rem' }}>{getStatusBadge(log.status)}</td>
                    <td style={{ padding: '0.5rem' }}>{log.faktura_id || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Informacje */}
      <div style={{
        backgroundColor: 'var(--info-light)',
        border: '1px solid var(--info-light)',
        borderRadius: '0.5rem',
        padding: '1rem'
      }}>
        <h4 style={{ marginBottom: '0.5rem' }}>Informacje o KSeF</h4>
        <ul style={{ margin: 0, paddingLeft: '1.25rem', lineHeight: '1.6' }}>
          <li><strong>KSeF</strong> - Krajowy System e-Faktur Ministerstwa Finansow</li>
          <li>Od 1 lutego 2026 obowiazkowy dla duzych firm</li>
          <li>Token autoryzacyjny wygenerujesz w portalu <a href="https://ksef.mf.gov.pl" target="_blank" rel="noopener noreferrer">ksef.mf.gov.pl</a></li>
          <li>Srodowisko testowe pozwala na testowanie bez konsekwencji</li>
          <li>Do 31.12.2026 tokeny dzialaja rownolegle z certyfikatami</li>
        </ul>
      </div>
    </div>
  );
};

export default KSeFSettings;
